/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-08-06
 */

// Add local dependencies.
import {
  validateEmail,
  validatePassword,
  validateRequired,
  validateName,
  validatePhone,
  validatePasswordMatch,
  validateCheckbox,
  validateUrl,
  validateBio,
  validateTitle,
  validateCompany,
  type ValidationRules,
} from './validation';

/**
 * Validation schema for user registration form.
 * Includes all fields required for user sign-up.
 */
export const registrationSchema: ValidationRules = {
  email: [validateEmail],
  firstName: [(value: string) => validateName(value, 'First name')],
  lastName: [(value: string) => validateName(value, 'Last name')],
  phoneNumber: [validatePhone], // Optional field
  password: [validatePassword],
  confirmPassword: [
    (value: string, formData: any) =>
      validatePasswordMatch(formData?.password || '', value),
  ],
  termsAccepted: [
    (checked: boolean) =>
      validateCheckbox(checked, 'You must accept the Terms of Service'),
  ],
  privacyAccepted: [
    (checked: boolean) =>
      validateCheckbox(checked, 'You must accept the Privacy Policy'),
  ],
  // marketingEmails is optional, no validation needed
};

/**
 * Validation schema for user login form.
 * Simple validation for email and password fields.
 * The password is only required: only the server checks an existing
 * password, and older or admin-set ones may not meet today's sign-up rules
 * (FE todo/44).
 */
export const loginSchema: ValidationRules = {
  email: [validateEmail],
  password: [(value: string) => validateRequired(value, 'Password')],
  // rememberMe is optional, no validation needed
};

/**
 * Validation schema for password reset request form.
 * Only requires email validation.
 */
export const passwordResetRequestSchema: ValidationRules = {
  email: [validateEmail],
};

/**
 * Validation schema for password reset confirmation form.
 * Validates new password and confirmation match.
 */
export const passwordResetConfirmSchema: ValidationRules = {
  password: [validatePassword],
  confirmPassword: [
    (value: string, formData: any) =>
      validatePasswordMatch(formData?.password || '', value),
  ],
};

/**
 * Validation schema for user profile form.
 * Includes personal information and professional fields.
 */
export const profileSchema: ValidationRules = {
  email: [validateEmail],
  firstName: [(value: string) => validateName(value, 'First name')],
  lastName: [(value: string) => validateName(value, 'Last name')],
  phoneNumber: [validatePhone], // Optional field
  title: [validateTitle], // Optional field
  company: [validateCompany], // Optional field
  bio: [validateBio], // Optional field
  website: [validateUrl], // Optional field
  linkedin: [validateUrl], // Optional field
  instagram: [validateUrl], // Optional field
  facebook: [validateUrl], // Optional field
};

/**
 * Validation schema for change password form.
 * Validates current password, new password, and confirmation.
 * The current password is only required, as on login (FE todo/44); the new
 * one must meet the sign-up rules.
 */
export const changePasswordSchema: ValidationRules = {
  currentPassword: [(value: string) => validateRequired(value, 'Password')],
  newPassword: [validatePassword],
  confirmNewPassword: [
    (value: string, formData: any) =>
      validatePasswordMatch(formData?.newPassword || '', value),
  ],
};

/**
 * Validation schema for email verification request form.
 * Only requires email validation.
 */
export const emailVerificationSchema: ValidationRules = {
  email: [validateEmail],
};

/**
 * Collection of all validation schemas for easy access.
 * Provides a centralized way to access validation rules for different forms.
 *
 * @example
 * import { validationSchemas } from './validationSchemas';
 * const { errors, validateForm } = useFormValidation(validationSchemas.registration);
 */
export const validationSchemas = {
  registration: registrationSchema,
  login: loginSchema,
  passwordResetRequest: passwordResetRequestSchema,
  passwordResetConfirm: passwordResetConfirmSchema,
  profile: profileSchema,
  changePassword: changePasswordSchema,
  emailVerification: emailVerificationSchema,
} as const;

/**
 * Type for validation schema keys.
 * Useful for type-safe schema selection.
 */
export type ValidationSchemaKey = keyof typeof validationSchemas;

/**
 * Helper function to get a validation schema by key.
 * Provides type-safe access to validation schemas.
 *
 * @param schemaKey - The key of the validation schema to retrieve
 * @returns The validation rules for the specified schema
 *
 * @example
 * const schema = getValidationSchema('registration');
 * const { errors, validateForm } = useFormValidation(schema);
 */
export const getValidationSchema = (
  schemaKey: ValidationSchemaKey
): ValidationRules => validationSchemas[schemaKey];

export default validationSchemas;
